import React, { useState, useMemo, useEffect } from 'react';
import {
  Container,
  Grid as GridLegacy,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  Select,
  MenuItem,
  TextField,
  LinearProgress,
  SelectChangeEvent,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  EmojiEvents as TrophyIcon,
  Timeline as TimelineIcon,
  Flag as FlagIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { Pie } from 'react-chartjs-2';
import { format, subDays, subWeeks, subMonths, startOfWeek, endOfWeek, parse, startOfMonth, endOfMonth } from 'date-fns';
import api from '../services/api';
import '../utils/chartSetup'; // Initialize Chart.js

// TypeScript Interfaces
interface User {
  username: string;
  role: string;
  team?: string;
  sales_tracker_id?: string;
  permission_group_id?: number | null;
}


interface UserMapping {
  username: string;
  team?: string;
  sales_tracker_id?: string;
}

interface SalesRecord {
  [key: string]: any;
  '4'?: string;
  Date?: string;
  'Agent '?: string;
  Agent?: string;
}

interface SalesMetrics {
  lastUpdated?: string;
}

interface SalesInfo {
  data?: SalesRecord[];
  totalRecords?: number;
  headers?: string[];
}

interface AgentData {
  name: string;
  count: number;
  team: string | null;
}

interface PieChartData {
  name: string;
  value: number;
  color: string;
}

interface FilteredMetrics {
  totalSales: number;
  activeAgents: number;
  allAgents: AgentData[];
  pharmAnimalsTotal: number;
  caliCrushersTotal: number;
  goalDiggersTotal: number;
  weekendWarriorsTotal: number;
}

// ─── Team Config ────────────────────────────────────────────────────────────
const TEAM_CONFIG = {
  'Pharm Animals': {
    color: '#000000',
    gradient: 'linear-gradient(135deg, #000000 0%, #1a1a2e 50%, #0d0d0d 100%)',
    logo: '/logos/PharmAnimals.jpg',
    label: 'Pharm Animals',
  },
  'Cali Crushers': {
    color: '#B8860B',
    gradient: 'linear-gradient(135deg, #C9A227 0%, #DAA520 50%, #A67C00 100%)',
    logo: '/logos/CaliCrushers.jpg',
    label: 'Cali Crushers',
  },
  'Goal Diggers': {
    color: '#1b5e20',
    gradient: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 50%, #43a047 100%)',
    logo: '/logos/GoalDiggers.jpg',
    label: 'Goal Diggers',
  },
  'Weekend Warriors': {
    color: '#C0582D',
    gradient: 'linear-gradient(135deg, #7B3118 0%, #C0582D 50%, #D4693E 100%)',
    logo: '/logos/ww.png',
    label: 'Weekend Warriors',
  },
};

const getTeamConfig = (team: string) =>
  TEAM_CONFIG[team as keyof typeof TEAM_CONFIG] || {
    color: '#616161',
    gradient: 'linear-gradient(135deg, #424242 0%, #616161 100%)',
    logo: '',
    label: team,
  };

// Constants
const COLORS = ['#1976d2', '#dc004e', '#388e3c', '#f57c00', '#7b1fa2', '#d32f2f', '#0288d1', '#c2185b'];
const DAILY_GOAL = 25;
// Per-team daily goal overrides. Teams not listed here fall back to DAILY_GOAL.
const TEAM_DAILY_GOALS: Record<string, number> = {
  'Weekend Warriors': 45,
};
const getTeamGoal = (team?: string): number =>
  (team && TEAM_DAILY_GOALS[team]) || DAILY_GOAL;

// ─── Temporarily hidden teams ─────────────────────────────────────────────
// Teams listed here are hidden from the Analytics dashboard (daily-goal
// cards, team battle, agent rankings, and distribution) WITHOUT deleting any
// data or users. Remove a team from this list to make it visible again.
// Goal Diggers was re-enabled (W2 team revival) — currently no teams hidden.
const HIDDEN_TEAMS: string[] = [];

const Analytics: React.FC = () => {
  const [dateFilter, setDateFilter] = useState<string>('today');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [allUsers, setAllUsers] = useState<UserMapping[]>([]);

  // Get current user from localStorage
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user: User = JSON.parse(userStr);
        setCurrentUser(user);
      } catch (e) {
        console.error('Error parsing user:', e);
      }
    }
  }, []);

  // Fetch all users to map agent names to teams
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5010'}/api/user-management/users/team-mapping`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        const data = await response.json();
        console.log('📊 Team mapping response:', data);
        if (data.success) {
          console.log('✅ Loaded users for team mapping:', data.users.length, 'users');
          console.log('Sample user:', data.users[0]);
          setAllUsers(data.users);
        }
      } catch (error) {
        console.error('❌ Error fetching users for team mapping:', error);
      }
    };
    fetchUsers();
  }, []);

  const {
    data: metricsData,
    isLoading: metricsLoading,
    error: metricsError,
  } = useQuery<{ metrics: SalesMetrics }>({
    queryKey: ['salesMetrics'],
    queryFn: () => api.getSalesMetrics(),
    refetchInterval: 5 * 60 * 1000,
  });

  const {
    data: salesData,
    isLoading: salesLoading,
    error: salesError,
  } = useQuery<{ data: SalesInfo }>({
    queryKey: ['salesData'],
    queryFn: () => api.getSalesData(),
    refetchInterval: 5 * 60 * 1000,
  });

  const metrics: SalesMetrics = metricsData?.metrics || {};
  const salesInfo: SalesInfo = salesData?.data || {};
  const rawData: SalesRecord[] = salesInfo.data || [];

  const parseSheetDate = (dateString: string): Date | null => {
    if (!dateString) return null;

    try {
      let parsedDate: Date | null = null;

      const dashMatch = dateString.match(/(\d+)-([A-Za-z]+)-(\d+)/);
      if (dashMatch) {
        const day = parseInt(dashMatch[1], 10);
        const monthStr = dashMatch[2];
        let year = parseInt(dashMatch[3], 10);

        if (year < 100) {
          year += 2000;
        }

        const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
        const monthIndex = monthNames.indexOf(monthStr.toLowerCase());

        if (monthIndex !== -1) {
          parsedDate = new Date(year, monthIndex, day);
        }
      } else if (dateString.includes('/')) {
        const slashMatch = dateString.match(/(\d+)\/(\d+)\/(\d+)/);
        if (slashMatch) {
          const day = parseInt(slashMatch[1], 10);
          const month = parseInt(slashMatch[2], 10) - 1;
          const year = parseInt(slashMatch[3], 10);
          parsedDate = new Date(year, month, day);
        }
      } else if (dateString.includes(',')) {
        let correctedDateString = dateString;

        if (dateString.includes('2027')) {
          correctedDateString = dateString.replace('2027', '2025');
        }

        parsedDate = parse(correctedDateString, 'MMM dd, yyyy', new Date());
      } else {
        parsedDate = new Date(dateString);
      }

      if (parsedDate && !isNaN(parsedDate.getTime())) {
        const currentYear = new Date().getFullYear();
        const parsedYear = parsedDate.getFullYear();

        if (parsedYear > currentYear + 1) {
          return null;
        }

        return parsedDate;
      }

      return null;
    } catch (e) {
      console.error('Error parsing date:', dateString, e);
      return null;
    }
  };

  const normalizeDate = (date: Date | string | null): string | null => {
    if (!date) return null;
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return null;

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const filteredData = useMemo(() => {
    if (dateFilter === 'all') return rawData;

    const now = new Date();

    return rawData.filter(record => {
      const dateValue = record['4'] || record.Date;
      if (!dateValue) return false;

      const recordDate = parseSheetDate(dateValue);
      if (!recordDate || isNaN(recordDate.getTime())) return false;

      const normalizedRecordDate = normalizeDate(recordDate);

      switch (dateFilter) {
        case 'today':
          const todayNorm = normalizeDate(now);
          return normalizedRecordDate === todayNorm;
        case 'previousDay':
          const yesterdayNorm = normalizeDate(subDays(now, 1));
          return normalizedRecordDate === yesterdayNorm;
        case 'currentWeek':
          const weekStart = startOfWeek(now, { weekStartsOn: 1 });
          const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
          const weekStartNorm = normalizeDate(weekStart);
          const weekEndNorm = normalizeDate(weekEnd);
          return normalizedRecordDate! >= weekStartNorm! && normalizedRecordDate! <= weekEndNorm!;
        case 'previousWeek':
          const currentWeekStart = startOfWeek(now, { weekStartsOn: 1 });
          const pastWeekStart = subWeeks(currentWeekStart, 1);
          const pastWeekEnd = subDays(currentWeekStart, 1);
          const pastWeekStartNorm = normalizeDate(pastWeekStart);
          const pastWeekEndNorm = normalizeDate(pastWeekEnd);
          return normalizedRecordDate! >= pastWeekStartNorm! && normalizedRecordDate! <= pastWeekEndNorm!;
        case 'currentMonth':
          const monthStart = startOfMonth(now);
          const monthEnd = endOfMonth(now);
          const monthStartNorm = normalizeDate(monthStart);
          const monthEndNorm = normalizeDate(monthEnd);
          return normalizedRecordDate! >= monthStartNorm! && normalizedRecordDate! <= monthEndNorm!;
        case 'previousMonth':
          const prevMonthStart = startOfMonth(subMonths(now, 1));
          const prevMonthEnd = endOfMonth(subMonths(now, 1));
          const prevMonthStartNorm = normalizeDate(prevMonthStart);
          const prevMonthEndNorm = normalizeDate(prevMonthEnd);
          return normalizedRecordDate! >= prevMonthStartNorm! && normalizedRecordDate! <= prevMonthEndNorm!;
        case 'dateRange':
          if (!startDate || !endDate) return false;
          const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
          const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
          const rangeStart = new Date(startYear, startMonth - 1, startDay);
          const rangeEnd = new Date(endYear, endMonth - 1, endDay);
          const rangeStartNorm = normalizeDate(rangeStart);
          const rangeEndNorm = normalizeDate(rangeEnd);
          return normalizedRecordDate! >= rangeStartNorm! && normalizedRecordDate! <= rangeEndNorm!;
        default:
          return true;
      }
    });
  }, [rawData, dateFilter, startDate, endDate]);

  // Helper to get team for an agent by matching sales_tracker_id
  const getAgentTeam = (agentName: string): string | null => {
    if (!agentName) return null;
    const normalizedAgentName = agentName.toLowerCase().trim();

    // Try matching by sales_tracker_id first (primary match)
    let user = allUsers.find(u => {
      if (!u.sales_tracker_id) return false;
      return u.sales_tracker_id.toLowerCase().trim() === normalizedAgentName;
    });

    // Fallback to username match if not found by sales_tracker_id
    if (!user) {
      user = allUsers.find(u => {
        if (!u.username) return false;
        return u.username.toLowerCase().trim() === normalizedAgentName;
      });
    }

    return user?.team || null;
  };

  // Calculate metrics with team information
  const filteredMetrics = useMemo((): FilteredMetrics => {
    const agentCounts: Record<string, number> = {};

    console.log('📈 Filtered data count:', filteredData.length);

    filteredData.forEach(record => {
      const agent = (record['Agent '] || record.Agent || 'Unknown').trim();

      if (agent && agent !== 'Unknown' && agent !== '') {
        agentCounts[agent] = (agentCounts[agent] || 0) + 1;
      }
    });

    console.log('👥 Agent counts:', agentCounts);
    console.log('🗂️ Available users for mapping:', allUsers.length);

    // All agents ranked by sales count with team info
    const allAgents: AgentData[] = Object.entries(agentCounts)
      .map(([name, count]) => {
        const team = getAgentTeam(name);
        console.log(`🔍 Mapping agent "${name}" -> team: ${team}`);
        return { name, count, team };
      })
      .sort((a, b) => b.count - a.count)
      // Exclude agents belonging to temporarily hidden teams (data kept, just hidden)
      .filter(a => !a.team || !HIDDEN_TEAMS.includes(a.team));

    console.log('✅ All agents with teams:', allAgents);

    // Team totals
    const pharmAnimalsTotal = allAgents
      .filter(a => a.team === 'Pharm Animals')
      .reduce((sum, a) => sum + a.count, 0);

    const caliCrushersTotal = allAgents
      .filter(a => a.team === 'Cali Crushers')
      .reduce((sum, a) => sum + a.count, 0);

    const goalDiggersTotal = allAgents
      .filter(a => a.team === 'Goal Diggers')
      .reduce((sum, a) => sum + a.count, 0);

    const weekendWarriorsTotal = allAgents
      .filter(a => a.team === 'Weekend Warriors')
      .reduce((sum, a) => sum + a.count, 0);

    return {
      totalSales: filteredData.length,
      activeAgents: Object.keys(agentCounts).length,
      allAgents,
      pharmAnimalsTotal,
      caliCrushersTotal,
      goalDiggersTotal,
      weekendWarriorsTotal,
    };
  }, [filteredData, allUsers]);

  // ─── Visibility flags ────────────────────────────────────────────────────
  // isAdmin           → superuser / manager: sees all visible teams everywhere
  // isCompetingTeam   → Pharm Animals / Cali Crushers / Goal Diggers: sees team cards + team battle,
  //                     but agent rankings/distribution scoped to own team
  // isWeekendWarriors → only sees own-team agent data, no cards, no battle
  const isAdmin = currentUser?.role === 'superuser' || currentUser?.role === 'manager';
  const isCompetingTeam =
    currentUser?.team === 'Pharm Animals' ||
    currentUser?.team === 'Cali Crushers' ||
    currentUser?.team === 'Goal Diggers';
  const isWeekendWarriors = currentUser?.team === 'Weekend Warriors';

  // Managers can belong to a specific team but still need full, cross-team
  // visibility of the sales data (summary cards, distribution pie, leaderboard).
  // They may be identified either by their `role` or by their permission group
  // (1 = Superuser, 2 = Manager) — mirroring the checks used elsewhere in the app.
  const isManager =
    currentUser?.role === 'manager' ||
    currentUser?.role === 'superuser' ||
    currentUser?.permission_group_id === 1 ||
    currentUser?.permission_group_id === 2;

  // Controls the BOTTOM "sales info" sections (summary cards, distribution pie,
  // leaderboard): admins and managers see every team; regular members see only
  // their own team. The TOP sections (daily-goal cards / team battle / header
  // logo) intentionally stay driven by `isAdmin`/team so they remain unchanged.
  const canViewAllData = isAdmin || isManager;

  const userTeam = currentUser?.team;



  // Filter agents by user's team.
  // Admins AND managers see all agents across every team; regular team members
  // only see their own team.
  const teamAgents = useMemo(() => {
    if (canViewAllData || !userTeam) {
      return filteredMetrics.allAgents;
    }
    // Strictly filter: only show agents with the EXACT team match
    return filteredMetrics.allAgents.filter(agent => agent.team && agent.team === userTeam);
  }, [filteredMetrics.allAgents, canViewAllData, userTeam]);


  // Get current user's sales count by matching sales_tracker_id
  const userSalesCount = useMemo(() => {
    if (!currentUser) return 0;
    // Try matching by sales_tracker_id first
    let userAgent = teamAgents.find(a => {
      if (!currentUser.sales_tracker_id) return false;
      return a.name.toLowerCase().trim() === currentUser.sales_tracker_id.toLowerCase().trim();
    });
    // Fallback to username match
    if (!userAgent) {
      userAgent = teamAgents.find(a => a.name.toLowerCase().trim() === currentUser.username.toLowerCase().trim());
    }
    return userAgent?.count || 0;
  }, [teamAgents, currentUser]);

  // Pie chart data
  const agentPieData = useMemo((): PieChartData[] =>
    teamAgents.slice(0, 8).map((agent, index) => ({
      name: agent.name,
      value: agent.count,
      color: COLORS[index % COLORS.length],
    })), [teamAgents]);

  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const handleDateFilterChange = (event: SelectChangeEvent<string>) => {
    setDateFilter(event.target.value);
  };

  const getFilterLabel = (): string => {
    switch (dateFilter) {
      case 'today':
        return 'Today';
      case 'previousDay':
        return 'Previous Day';
      case 'currentWeek':
        return 'Current Week';
      case 'previousWeek':
        return 'Previous Week';
      case 'currentMonth':
        return 'Current Month';
      case 'previousMonth':
        return 'Previous Month';
      case 'dateRange':
        if (startDate && endDate) {
          const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
          const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
          const start = format(new Date(startYear, startMonth - 1, startDay), 'MMM dd, yyyy');
          const end = format(new Date(endYear, endMonth - 1, endDay), 'MMM dd, yyyy');
          return startDate === endDate ? start : `${start} - ${end}`;
        }
        return 'Date Range';
      case 'all':
        return 'All Time';
      default:
        return '';
    }
  };

  // ─── Helper: team bg colour for agent-ranking chips ──────────────────────
  const getTeamChipColor = (team: string | null): string => {
    if (!team) return '#616161';
    const cfg = TEAM_CONFIG[team as keyof typeof TEAM_CONFIG];
    return cfg ? cfg.color : '#616161';
  };

  // ─── Team total resolver ─────────────────────────────────────────────────
  const getTeamTotal = (team: string): number => {
    switch (team) {
      case 'Pharm Animals':
        return filteredMetrics.pharmAnimalsTotal;
      case 'Cali Crushers':
        return filteredMetrics.caliCrushersTotal;
      case 'Goal Diggers':
        return filteredMetrics.goalDiggersTotal;
      case 'Weekend Warriors':
        return filteredMetrics.weekendWarriorsTotal;
      default:
        return 0;
    }
  };

  // ─── Leading-team logic (all teams) ──────────────────────────────────────
  const teamTotalsMap: Record<string, number> = {
    'Pharm Animals': filteredMetrics.pharmAnimalsTotal,
    'Cali Crushers': filteredMetrics.caliCrushersTotal,
    'Goal Diggers': filteredMetrics.goalDiggersTotal,
    'Weekend Warriors': filteredMetrics.weekendWarriorsTotal,
  };
  // Exclude temporarily hidden teams from the leading-team calculation
  HIDDEN_TEAMS.forEach(team => {
    delete teamTotalsMap[team];
  });

  const maxTotal = Math.max(...Object.values(teamTotalsMap));
  const leadingTeams = Object.entries(teamTotalsMap).filter(([, v]) => v === maxTotal);
  const leadingTeam = leadingTeams.length === 1 ? leadingTeams[0][0] : 'Tied';

  // Convenience: gap between the leader and 2nd place (for the chip label)
  const sortedTotals = Object.values(teamTotalsMap).sort((a, b) => b - a);
  const leadGap = sortedTotals[0] - sortedTotals[1];

  // ─── Whether to show the multi-team sections ─────────────────────────────
  const showTeamSections = isAdmin || isCompetingTeam;

  // ─── Ordered team list for battle / cards (hidden teams excluded) ────────
  const ALL_BATTLE_TEAMS = ['Pharm Animals', 'Cali Crushers', 'Goal Diggers', 'Weekend Warriors'];
  const BATTLE_TEAMS = ALL_BATTLE_TEAMS.filter(team => !HIDDEN_TEAMS.includes(team));

  if (metricsLoading || salesLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (metricsError || salesError) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Failed to load analytics data. Please check your connection and try again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center" gap={2}>
          {!isAdmin && userTeam && (
            <Box
              component="img"
              src={getTeamConfig(userTeam).logo}
              alt={`${userTeam} Logo`}
              sx={{
                width: 150,
                height: 150,
                borderRadius: 2,
                objectFit: 'cover',
                border: '4px solid',
                borderColor: getTeamConfig(userTeam).color,
                boxShadow: '0 6px 12px rgba(0,0,0,0.3)',
              }}
            />
          )}
          <Box>
            <Typography variant="h4">Analytics</Typography>
          </Box>
        </Box>

        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
            <Select
              value={dateFilter}
              onChange={handleDateFilterChange}
              displayEmpty
              sx={{ '& .MuiSelect-select': { py: 1 } }}
            >
              <MenuItem value="" disabled>
                <em>Select Time Period</em>
              </MenuItem>
              <MenuItem value="today">Current Day</MenuItem>
              <MenuItem value="previousDay">Previous Day</MenuItem>
              <MenuItem value="currentWeek">Current Week</MenuItem>
              <MenuItem value="previousWeek">Previous Week</MenuItem>
              <MenuItem value="currentMonth">Current Month</MenuItem>
              <MenuItem value="previousMonth">Previous Month</MenuItem>
              <MenuItem value="dateRange">Date Range</MenuItem>
              <MenuItem value="all">All Time</MenuItem>
            </Select>
          </FormControl>
          {dateFilter === 'dateRange' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
              <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                From:
              </Typography>
              <TextField
                type="date"
                size="small"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                sx={{ minWidth: { xs: '130px', sm: 150 } }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: endDate || undefined, placeholder: 'Start date' }}
              />
              <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                To:
              </Typography>
              <TextField
                type="date"
                size="small"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                sx={{ minWidth: { xs: '130px', sm: 150 } }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: startDate || undefined, placeholder: 'End date' }}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Daily Goal Cards — one per visible team (admin + competing teams, today only) */}
      {showTeamSections && dateFilter === 'today' && (
        <GridLegacy container spacing={3} sx={{ mb: 3 }}>
          {BATTLE_TEAMS.map(teamName => {
            const cfg = getTeamConfig(teamName);
            const total = getTeamTotal(teamName);
            const goal = getTeamGoal(teamName);

            return (
              <GridLegacy key={teamName} size={{ xs: 12, sm: 6, md: Math.max(3, 12 / BATTLE_TEAMS.length) }}>
                <Card sx={{ background: cfg.gradient }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Box
                        component="img"
                        src={cfg.logo}
                        alt={`${teamName} Logo`}
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 1,
                          objectFit: 'cover',
                          border: '2px solid #FFD700',
                          cursor: 'pointer',
                          transition: 'transform 0.25s ease',
                          transformOrigin: 'left center',
                          '&:hover': {
                            transform: 'scale(2)',
                            position: 'relative',
                            zIndex: 20,
                            boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
                          },
                        }}
                      />
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                        {teamName} Daily Goal: {goal}
                      </Typography>
                    </Box>
                    <Typography variant="h3" sx={{ color: 'white', fontWeight: 700, mb: 2 }}>
                      {total} / {goal}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min((total / goal) * 100, 100)}
                      sx={{
                        height: 20,
                        borderRadius: 2,
                        bgcolor: 'rgba(255,255,255,0.3)',
                        '& .MuiLinearProgress-bar': { bgcolor: '#4caf50', borderRadius: 2 },
                      }}
                    />
                    <Typography variant="body2" sx={{ color: 'white', mt: 1, textAlign: 'center' }}>
                      {Math.min(Math.round((total / goal) * 100), 100)}% Complete
                      {total >= goal && ' 🎉'}
                    </Typography>
                  </CardContent>
                </Card>
              </GridLegacy>
            );
          })}
        </GridLegacy>
      )}

      {/* ── Single-team daily card (Weekend Warriors only, today) ────────── */}
      {isWeekendWarriors && dateFilter === 'today' && userTeam && (
        <GridLegacy container spacing={3} sx={{ mb: 3 }}>
          <GridLegacy size={{ xs: 12 }}>
            <Card sx={{ background: getTeamConfig(userTeam).gradient }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                  <Box display="flex" alignItems="center">
                    <FlagIcon sx={{ mr: 2, fontSize: 50, color: 'white' }} />
                    <Box>
                      <Typography variant="h5" sx={{ color: 'white', fontWeight: 600 }}>
                        Team {userTeam} Daily Goal: {getTeamGoal(userTeam)}
                      </Typography>
                      <Typography variant="h3" sx={{ color: 'white', fontWeight: 700, mt: 1 }}>
                        {filteredMetrics.weekendWarriorsTotal} / {getTeamGoal(userTeam)}
                      </Typography>
                      <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', mt: 1 }}>
                        {filteredMetrics.weekendWarriorsTotal >= getTeamGoal(userTeam)
                          ? '🎉 Goal Achieved!'
                          : `${getTeamGoal(userTeam) - filteredMetrics.weekendWarriorsTotal} more to go!`}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ width: { xs: '100%', sm: '40%' }, minWidth: 200 }}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min((filteredMetrics.weekendWarriorsTotal / getTeamGoal(userTeam)) * 100, 100)}
                      sx={{
                        height: 20,
                        borderRadius: 2,
                        bgcolor: 'rgba(255,255,255,0.3)',
                        '& .MuiLinearProgress-bar': { bgcolor: '#4caf50', borderRadius: 2 },
                      }}
                    />
                    <Typography variant="body2" sx={{ color: 'white', mt: 1, textAlign: 'center' }}>
                      {Math.min(Math.round((filteredMetrics.weekendWarriorsTotal / getTeamGoal(userTeam)) * 100), 100)}% Complete
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </GridLegacy>
        </GridLegacy>
      )}

      {/* ── 3-Team Battle (admin + competing teams only) ──────────────────── */}
      {showTeamSections && (
        <GridLegacy container spacing={3} sx={{ mb: 4 }}>
          <GridLegacy size={{ xs: 12 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom sx={{ textAlign: 'center', fontWeight: 600, mb: 3 }}>
                Team Battle — {getFilterLabel()}
              </Typography>

              <GridLegacy container spacing={2} alignItems="stretch">
                {BATTLE_TEAMS.map(teamName => {
                  const cfg = getTeamConfig(teamName);
                  const total = getTeamTotal(teamName);
                  const isLeading = leadingTeam === teamName;

                  return (
                    <GridLegacy key={teamName} size={{ xs: 12, sm: 6, md: Math.max(3, 12 / BATTLE_TEAMS.length) }}>
                      <Card
                        sx={{
                          bgcolor: cfg.color,
                          color: 'white',
                          transform: isLeading ? 'scale(1.05)' : 'scale(1)',
                          transition: 'all 0.3s',
                          border: isLeading ? '3px solid #FFD700' : '3px solid #D4AF37',
                          position: 'relative',
                          overflow: 'visible',
                          height: '100%',
                        }}
                      >
                        <CardContent sx={{ textAlign: 'center', py: 3 }}>
                          <Box
                            component="img"
                            src={cfg.logo}
                            alt={`${teamName} Logo`}
                            sx={{
                              position: 'absolute',
                              top: 10,
                              left: 10,
                              width: 55,
                              height: 55,
                              borderRadius: 1,
                              objectFit: 'cover',
                              border: '2px solid #FFD700',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                              cursor: 'pointer',
                              transition: 'transform 0.25s ease',
                              transformOrigin: 'top left',
                              '&:hover': {
                                transform: 'scale(2.2)',
                                zIndex: 30,
                                boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
                              },
                            }}
                          />
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, mt: 1 }}>
                            {teamName}
                          </Typography>
                          <Typography variant="h2" sx={{ fontWeight: 700, my: 2 }}>
                            {total}
                          </Typography>
                          {isLeading && leadGap > 0 && (
                            <Chip
                              label={`+${leadGap} ahead`}
                              icon={<TrendingUpIcon />}
                              sx={{
                                bgcolor: '#FFD700',
                                color: '#000',
                                fontWeight: 700,
                                fontSize: '1rem',
                                height: 36,
                                '& .MuiChip-icon': { color: '#000' },
                              }}
                            />
                          )}
                          <Typography variant="body2" sx={{ mt: 2, opacity: 0.9 }}>
                            Total Sales
                          </Typography>
                        </CardContent>
                      </Card>
                    </GridLegacy>
                  );
                })}
              </GridLegacy>

              {leadingTeam !== 'Tied' && (
                <Alert
                  severity="info"
                  sx={{ mt: 3, fontSize: '1.1rem', fontWeight: 600 }}
                  icon={<TrophyIcon fontSize="large" />}
                >
                  {leadingTeam} is currently in the lead by {leadGap} sales!
                </Alert>
              )}

              {leadingTeam === 'Tied' && (
                <Alert severity="warning" sx={{ mt: 3, fontSize: '1.1rem', fontWeight: 600 }}>
                  It&apos;s a tie! Teams are level at {maxTotal} sales!
                </Alert>
              )}
            </Paper>
          </GridLegacy>
        </GridLegacy>
      )}

      {/* ── Summary Cards ───────────────────────────────────────────────── */}
      <GridLegacy container spacing={3} sx={{ mb: 4 }}>
        <GridLegacy size={{ xs: 12, sm: 6, md: 4 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TimelineIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    {canViewAllData ? 'Total Sales' : `${userTeam || 'Your Team'} Sales`}
                  </Typography>
                  <Typography variant="h5">
                    {formatNumber(
                      canViewAllData
                        ? filteredMetrics.totalSales
                        : userTeam === 'Pharm Animals'
                        ? filteredMetrics.pharmAnimalsTotal

                        : userTeam === 'Cali Crushers'
                        ? filteredMetrics.caliCrushersTotal
                        : userTeam === 'Goal Diggers'
                        ? filteredMetrics.goalDiggersTotal
                        : userTeam === 'Weekend Warriors'
                        ? filteredMetrics.weekendWarriorsTotal
                        : 0
                    )}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {getFilterLabel()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </GridLegacy>

        <GridLegacy size={{ xs: 12, sm: 6, md: 4 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PeopleIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    {canViewAllData ? 'Active Agents' : 'Team Members'}
                  </Typography>

                  <Typography variant="h5">
                    {formatNumber(teamAgents.length)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {getFilterLabel()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </GridLegacy>

        <GridLegacy size={{ xs: 12, sm: 6, md: 4 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUpIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Avg Sales per Agent
                  </Typography>
                  <Typography variant="h5">
                    {teamAgents.length > 0
                      ? formatNumber(Math.round(teamAgents.reduce((sum, a) => sum + a.count, 0) / teamAgents.length))
                      : '0'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {getFilterLabel()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </GridLegacy>
      </GridLegacy>

      {/* ── Agent Distribution + Rankings ───────────────────────────────── */}
      <GridLegacy container spacing={3}>
        {/* Agent Distribution Pie Chart */}
        <GridLegacy size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {canViewAllData ? 'Agent Distribution' : `${userTeam || 'Team'} Distribution`}

            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Top 8 agents by sales count
            </Typography>
            {agentPieData.length > 0 ? (
              <Box sx={{ height: 400 }}>
                <Pie
                  data={{
                    labels: agentPieData.map(d => d.name),
                    datasets: [{
                      data: agentPieData.map(d => d.value),
                      backgroundColor: agentPieData.map(d => d.color),
                      borderColor: '#fff',
                      borderWidth: 2,
                    }],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: true,
                        position: 'right' as const,
                        labels: { font: { size: 12 } },
                      },
                      tooltip: {
                        callbacks: {
                          label: (context) => {
                            const label = context.label || '';
                            const value = context.parsed || 0;
                            return `${label}: ${value} sales`;
                          },
                        },
                      },
                    },
                  }}
                />
              </Box>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                <Typography color="textSecondary">No agent data available for this period</Typography>
              </Box>
            )}
          </Paper>
        </GridLegacy>

        {/* Agent Rankings */}
        <GridLegacy size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2, maxHeight: 500, overflow: 'auto' }}>
            <Box display="flex" alignItems="center" mb={2}>
              <TrophyIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">
                {canViewAllData ? 'Agent Rankings' : `${userTeam || 'Team'} Leaderboard`}
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {canViewAllData ? 'All agents' : `${userTeam || 'Team'} members`} ranked by sales count for {getFilterLabel()}

            </Typography>
            <List dense>
              {teamAgents && teamAgents.length > 0 ? (
                teamAgents.map((agent, index) => (
                  <ListItem
                    key={index}
                    divider
                    sx={{
                      backgroundColor: index < 3 ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={`#${index + 1}`}
                            size="small"
                            color={index === 0 ? 'warning' : index === 1 ? 'default' : index === 2 ? 'default' : 'default'}
                            variant={index < 3 ? 'filled' : 'outlined'}
                            sx={{
                              minWidth: 45,
                              fontWeight: index < 3 ? 'bold' : 'normal',
                              backgroundColor: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : undefined,
                              color: index < 3 ? '#000' : undefined,
                            }}
                          />
                          <Typography variant="body1" fontWeight={index < 3 ? 'bold' : 'normal'}>
                            {agent.name}
                          </Typography>
                          {canViewAllData && agent.team && (

                            <Chip
                              label={agent.team}
                              size="small"
                              sx={{
                                bgcolor: getTeamChipColor(agent.team),
                                color: 'white',
                                fontSize: '0.7rem',
                              }}
                            />
                          )}
                        </Box>
                      }
                      secondary={`${agent.count} sales`}
                    />
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText primary="No agent data available for this period" />
                </ListItem>
              )}
            </List>
          </Paper>
        </GridLegacy>

        {/* Data Summary */}
        <GridLegacy size={{ xs: 12 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Data Summary
            </Typography>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <Chip
                label={`${salesInfo.totalRecords || 0} total records`}
                color="primary"
                variant="outlined"
              />
              <Chip
                label={`${salesInfo.headers?.length || 0} data columns`}
                color="secondary"
                variant="outlined"
              />
              <Chip
                label={`Last updated: ${metrics.lastUpdated
                  ? format(new Date(metrics.lastUpdated), 'MMM dd, yyyy HH:mm')
                  : 'Never'}`}
                color="info"
                variant="outlined"
              />
              <Typography variant="body2" color="textSecondary">
                Real-time sync with Google Sheets
              </Typography>
            </Box>
          </Paper>
        </GridLegacy>
      </GridLegacy>
    </Container>
  );
};

export default Analytics;
